import { findHeaderCol as findCol } from "../lib/xlsxHeaders"; // A15, brief 65
import { useState } from "react";
import { loadXlsx } from "../lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is read or written
import { xlsxRow } from "../lib/xlsxCells.mjs";
// 166 §G.3 — `outcomeVariant` joins; `fd`/`fq`/`fc`/`nid` were imported and
// never used (`nid` arrives as the `nidFn` PROP, :311), and this line is this
// branch's to clean because this branch edits it.
import React from "react"; // 166 §G — `react/react-in-jsx-scope`; see the note below
import { pname, outcomeVariant } from "../utils";
import { tImperative as _tImp, useTranslation } from "../i18n";
import { FS } from "../lib/tokens";
import { ModalLayout } from "./SharedUI";
import FailedRowsList, { FAILED_ROWS_MAX } from "./FailedRowsList"; // 166 §G.2
import { computeEntryFees } from "../api";
import { redEFCo, redEFExportRow, redEFMatter, redRoleRows } from "../lib/financialsClient";
import Table from "./Table";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`
import ImportLossDialog from "./ImportLossDialog"; // 214 addendum 4 §25 [RULED 63] — an import asks before it loses anything
import { addRowsTakenUnder, hasLoss, newLossPlan, textOfValue, type LossPlan } from "../lib/importLoss"; // 214 addendum 4 §25
import type { Translate } from "../lib/interestRefusal";

// European date formatting for Excel export
const fdExcel = (d: any) =>
  d
    ? new Date(d).toLocaleDateString("fr-BE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "";

// Parse flexible date formats: DD/MM/YYYY, YYYY-MM-DD, DD-MM-YYYY, Excel serial
const parseDate = (v: any) => {
  if (!v) return null;
  if (typeof v === "number") {
    // Excel serial date
    const d = new Date((v - 25569) * 86400000);
    return d.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  // DD/MM/YYYY or DD-MM-YYYY
  const m1 = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m1) return `${m1[3]}-${m1[2].padStart(2, "0")}-${m1[1].padStart(2, "0")}`;
  // YYYY-MM-DD
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return s;
  return null;
};

/* ── 166 §G.1 — THE MINUTES COLUMN READS A DURATION ───────────────────────────
 *
 * WHAT WENT WRONG. `doImport` read this column with `parseInt(v) || 0` and
 * dropped the row when that was <= 0. A lawyer's timesheet states durations as
 * Excel TIME cells, and `XLSX.utils.sheet_to_json` hands a time cell over as a
 * DAY FRACTION — 10 minutes is `0.006944444444444444`, which `parseInt` reads
 * as 0. So a 94-row file of real prestations imported nothing, and (166 §G.3)
 * said so in green.
 *
 * WHAT `sheet_to_json` ACTUALLY HANDS OVER, measured against a fixture built
 * with this same xlsx 0.20.3 and read exactly as `:216` reads it — TWO JS
 * types in this column, never three:
 *
 *     cell                     typeof     value                    parseInt
 *     time  0:10               number     0.006944444444444444     0
 *     time  7:30               number     0.3125                   0
 *     time  1:00               number     0.041666666666666664     0
 *     time 24:00               number     1                        1   ← !
 *     plain 90                 number     90                       90
 *     text "1:45"              string     "1:45"                   1
 *     text "12,5"              string     "12,5"                   12
 *     text "n/a"               string     "n/a"                    0
 *
 * THE ADDENDUM'S CASE IS REAL AND IS THE FOURTH ROW: `1` is "one minute" as a
 * number and "24:00" as a time cell, and no rule over the VALUE can separate
 * them. So the cell's own format decides, and the two detectors offered were
 * both measured:
 *
 *   (a) `cellNF: true` → `ws[addr].z` is "h:mm" / "[h]:mm" for the time cells
 *       and "General" for the rest. It works. It is NOT what this uses: `.z`
 *       is per-CELL and reaching it needs an ADDRESS, while `sheet_to_json`'s
 *       object mode drops blank rows — so the index→address mapping is exactly
 *       the kind of coordinate that silently slips by one and mislabels every
 *       row after a gap.
 *   (b) A SECOND `sheet_to_json(ws, { defval: "", raw: false })` pass, used
 *       only for this column. CHOSEN. Measured: the time cells arrive already
 *       formatted — "0:10", "7:30", "1:00", "24:00" — and "90" / "1:45" /
 *       "12,5" / "n/a" come through unchanged, so the H:MM branch reads all of
 *       them unambiguously. It produces a PARALLEL array by construction (same
 *       sheet, same options but one, same blank-row policy), so there is no
 *       mapping to get wrong, and `doImport` asserts the two lengths match
 *       before trusting it.
 *
 * The existing read at `:216` is NOT flipped to `raw: false`, as directed:
 * `parseDate` above is written against raw serials and a formatted date is
 * locale-shaped.
 *
 * The value rules stay as the FALLBACK, for a column with no format at all.
 * A duration is never GUESSED: a cell that is neither a duration nor a number
 * of minutes is refused, by row, with its own text in the reason (Laurent's
 * ruling 6, 2026-09-04).
 */
export interface MinutesCell {
  /** whole minutes, before the 5-minute grid; null = refused. */
  minutes: number | null;
  /** why it was refused, already translated; null when it was not. */
  reason: string | null;
  /** how it was read — the summary states this once, so a wrong reading is
   *  visible before it becomes 94 wrong rows. */
  source: "time" | "minutes" | null;
}

/** A parsed sheet row: `sheet_to_json`'s object mode, keyed by the sheet's own
 *  header text. Every value is a cell, and a cell is `unknown` until read. */
type SheetRow = Record<string, unknown>;

const HHMM = /^(\d{1,3}):([0-5]\d)$/;
const DECIMAL = /^\d+([.,]\d+)?$/;

/** The text a refusal shows back: a cell's own value, never `[object Object]`. */
function cellText(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (v == null) return "";
  return "?";
}

const refuseMinutes = (shown: string): MinutesCell => ({
  minutes: null,
  source: null,
  reason: _tImp("timeExcel.reason_not_a_duration", {
    value: shown,
    fallback: `not a duration: ${shown}`,
  }),
});

/** `H:MM` → whole minutes, or null when the text is not that shape. */
function hhmmMinutes(text: string): number | null {
  const m = HHMM.exec(text.trim());
  return m ? +m[1] * 60 + +m[2] : null;
}

/** The string branch: a formatted duration, a plain decimal, or a refusal. */
function minutesFromText(text: string): MinutesCell {
  if (!text) return refuseMinutes("");
  const hm = hhmmMinutes(text);
  if (hm != null) return { minutes: hm, reason: null, source: "time" };
  // A comma-or-dot decimal typed into a minutes column is MINUTES ("12,5"),
  // never a day fraction: a human wrote it, and they wrote what they meant.
  if (DECIMAL.test(text))
    return { minutes: Math.round(Number(text.replace(",", "."))), reason: null, source: "minutes" };
  return refuseMinutes(text);
}

export function parseMinutesCell(raw: unknown, formatted?: unknown): MinutesCell {
  const refuse = refuseMinutes;

  /* 1. THE FORMATTED PASS FIRST — it is the only thing that can tell 24:00
     from 1 minute, and it is right for every other time cell too. */
  const mf = hhmmMinutes(cellText(formatted));
  if (mf != null) return { minutes: mf, reason: null, source: "time" };

  /* 2. A Date, if a caller ever reads with `cellDates: true`. Measured: that
     option turns these same cells into `Sun Dec 31 1899 00:10:00`, so the
     wall-clock time IS the duration. Nothing in this file passes it today;
     the branch is here so a later reader change cannot silently zero the
     column the way the day fraction did. */
  if (raw instanceof Date)
    return { minutes: raw.getHours() * 60 + raw.getMinutes(), reason: null, source: "time" };

  /* 3. THE VALUE RULES, the fallback for an unformatted column. */
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw >= 1) return { minutes: Math.round(raw), reason: null, source: "minutes" };
    // Strictly between 0 and 1: an Excel day fraction. Nobody logs a fraction
    // of a minute, so there is no other reading of it.
    if (raw > 0) return { minutes: Math.round(raw * 1440), reason: null, source: "time" };
    return refuse(String(raw));
  }
  if (typeof raw === "string") return minutesFromText(raw.trim());
  return refuse(cellText(raw));
}

// Flexible header matching

// Exit 22 (brief 38 §9) — the export writes its headers, cells and sheet name
// from the dictionary in the exporter's language. The import half must then
// recognise those headers in EVERY supported language, not just the one this
// browser renders: the workbook a French-speaking partner exports is
// re-imported by a Dutch-speaking colleague, and an unrecognised column is a
// silently ignored column (exit 25's breakage class). The catalog behind
// window.__lxI18n holds all languages, so candidates are gathered across all
// of them, with the legacy hardcoded aliases kept for pre-slice workbooks.
const dictTexts = (key: string): string[] => {
  const data = typeof window !== "undefined" ? window.__lxI18n?.data : undefined;
  if (!data || typeof data !== "object") return [];
  return Object.values(data as Record<string, Record<string, unknown>>)
    .map((cat) => cat && cat[key])
    .filter((s): s is string => typeof s === "string" && s.length > 0);
};
const colNames = (k: string): string[] => dictTexts(`timeExcel.col.${k}`);

// C.2 — the Rate/Amount cell values are server-handed (POST
// /api/financials/entry-fees; same arithmetic, verbatim — including the
// family's only 2-dp rounding and the "" sentinels — recording-verified:
// recordings/c2-entryfees/). The export posts the rows it holds (reduced
// via src/lib/financialsClient.ts) plus the ca/cc arrays AS THE CALLER
// PASSES THEM (CaseView exports with cc:[], CCV with ca:[] — caller-side
// scoping is frozen behaviour) and writes the returned cell values
// verbatim. The sheet is only ever built from the response (export guard —
// no local fallback); on failure nothing is written and the toast says so.
// `roleRows` (useRoleLabels rows) replaces the old effR option.
// 149 §2.2 — the sheet carries the MARK, not its mirror. The 3-state
// billabilityMark is the real field; `billed` is a server-derived mirror of
// one of its values (lib/billability.js), and two columns saying one thing is
// how they drift. A row that predates the mark in a stale cache is read
// through the mirror's own invariant (billed === mark 'billable'), never
// guessed.
type MarkRow = { mark?: unknown; billed?: unknown };
const markOf = (e: MarkRow): "unmarked" | "billable" | "unbillable" =>
  e.mark === "billable" || e.mark === "unbillable" || e.mark === "unmarked"
    ? e.mark
    : e.billed
      ? "billable"
      : "unmarked";
const markCell = (e: MarkRow) => {
  const m = markOf(e);
  return m === "billable"
    ? _tImp("timeExcel.cell_mark_billable", { fallback: "Billable" })
    : m === "unbillable"
      ? _tImp("timeExcel.cell_mark_unbillable", { fallback: "Unbillable" })
      : _tImp("timeExcel.cell_mark_unmarked", { fallback: "Unmarked" });
};

export async function exportTimeEntries({
  entries,
  co,
  roleRows,
  ca,
  cc,
  inv,
  filename,
  toast,
}: any) {
  let handed;
  try {
    handed = (await computeEntryFees({
      collaborators: (co || []).map(redEFCo),
      roleRows: redRoleRows(roleRows || []),
      cases: (ca || []).map(redEFMatter),
      collectiveCases: (cc || []).map(redEFMatter),
      export: { rows: entries.map(redEFExportRow) },
    })) as { export?: { rate?: number | string; amount?: number | string }[] };
  } catch {
    if (toast)
      toast(
        _tImp("timeExcel.export_failed_compute", {
          fallback: "Export failed — could not compute amounts",
        }),
        "error", // 166 §G.3 — a failure, and it was green
      );
    return;
  }
  const cells = handed?.export || [];
  // Headers, the Billed cells and the sheet name come from the dictionary in
  // the exporter's language (exit 22). Nothing downstream parses this workbook
  // except our own ImportPreview, whose findCol candidates include every
  // language's headers (colNames above) — so the round trip survives the
  // translation instead of being broken by it.
  const H = (k: string, fallback: string) => _tImp(`timeExcel.col.${k}`, { fallback });
  const rows = entries.map((e: any, i: number) => {
    const c = co.find((x: any) => x.id === e.coId);
    const cell = cells[i] || { rate: "", amount: "" };
    return {
      [H("date", "Date")]: fdExcel(e.date),
      [H("collaborator", "Collaborator")]: c ? pname(c) : "",
      [H("activity_type", "Activity Type")]: e.type || "",
      [H("description", "Description")]: e.desc || "",
      [H("minutes", "Minutes")]: e.min || 0,
      [H("hourly_rate", "Hourly Rate (EUR)")]: cell.rate,
      [H("amount", "Amount (EUR)")]: cell.amount,
      // 149 §2.2 — `Billed` becomes `Mark` (the 3-state field itself); the old
      // header stays recognised by the import for sheets already exported.
      [H("mark", "Mark")]: markCell(e),
      [H("billed_date", "Billed Date")]: e.billedDate ? fdExcel(e.billedDate) : "",
      // 149 §2.2 — the invoice NUMBER where invoiceId is set, empty otherwise
      // (a draft pro forma without a number prints empty too). Exported as
      // information; the import refuses it — an invoice binding is the
      // server's, never a spreadsheet's.
      [H("invoice", "Invoice")]: e.invoiceId
        ? String(
            (
              (inv || []).find(
                (iv: { id?: number; number?: string | null }) => iv.id === e.invoiceId,
              ) || {}
            ).number || "",
          )
        : "",
    };
  });

  // LEGAL-LANE (round 2, Excel sweep) — guarded + reported. Descriptions are
  // free text, so this sheet is unbounded by construction.
  try {
    const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
    const ws = XLSX.utils.json_to_sheet(rows.map(xlsxRow));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      _tImp("timeExcel.sheet_name", { fallback: "Time Entries" }),
    );
    XLSX.writeFile(wb, filename);
    if (toast)
      toast(
        _tImp("timeExcel.exported_n", withCount(rows.length, "n", { fallback: "Exported {n} time entries",
        })),
      );
  } catch (e: any) {
    if (toast)
      toast(
        _tImp("timeExcel.export_failed", {
          message:
            e?.message || _tImp("timeExcel.unknown_error", { fallback: "unknown error" }),
          fallback: "⚠ Export failed: {message}",
        }),
        "error", // 166 §G.3 — a message that opens with "⚠ Export failed", green
      );
    else throw e;
  }
}

export function ImportPreview({
  file,
  co,
  tkTypes,
  caseId,
  ccId,
  te,
  sTe,
  s,
  SK,
  nid: nidFn,
  currentUser,
  onClose,
  toast,
  mode: defaultMode,
  matterLabel, // 214 addendum 4 §25 — the matter the window names (its ref)
}: any) {
  const { t: _t } = useTranslation();
  /* 166 §G — `useTranslation()`'s return type is INFERRED and its `t` lands
     here as `any`, so every call the new code makes reads to eslint as an
     unsafe call on an unsafe value. Typed ONCE, locally, rather than widening
     a hook two hundred files depend on — that is its own unit, not this one's.
     `React` is imported above for the same family of reasons: the committed
     config still enforces `react/react-in-jsx-scope`, the ratchet forbids a
     file's count from rising, and putting React in scope is the rule's own
     remedy rather than a way around it. */
  const t = _t as (k: string, o?: Record<string, unknown>) => string;
  /* 166 §G — TYPED, not `any[]`: the rows are objects keyed by the sheet's own
     headers, which is all `sheet_to_json` promises and all this file needs. */
  const [rows, setRows] = useState<SheetRow[]>([]);
  /* 166 §G.1 — the SAME rows, read with `raw: false`, used for the MINUTES
     column only (see `parseMinutesCell`'s header for why this and not
     `cellNF`). Parallel by construction; `doImport` asserts the lengths. */
  const [fmtRows, setFmtRows] = useState<SheetRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [importMode, setImportMode] = useState(defaultMode || "add");
  /* 166 §G.2 — the import's own verdict, so the refused rows can be READ. The
     modal used to close on both paths and the whole account of what happened
     was a toast; a 94-row refusal deserves a list. */
  const [outcome, setOutcome] = useState<{
    imported: number;
    total: number;
    skipped: { rn: number; who: string; desc: string; reason: string }[];
    readAs: string;
  } | null>(null);
  const [parsed, setParsed] = useState(false);
  const [error, setError] = useState<any>(null);
  // 214 addendum 4 §25 [RULED 63] — the window's plan and the write it waits for
  const [lossAsk, setLossAsk] = useState<{ plan: LossPlan; run: () => void } | null>(null);

  // Parse file on mount
  useState(() => {
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
        const wb = XLSX.read((ev.target as any).result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<SheetRow>(ws, { defval: "" });
        if (!json.length) {
          setError(t("timeExcel.empty_file", { fallback: "Empty file" }));
          return;
        }
        setHeaders(Object.keys(json[0]));
        setRows(json);
        setFmtRows(XLSX.utils.sheet_to_json<SheetRow>(ws, { defval: "", raw: false }));
        setParsed(true);
      } catch (err) {
        setError(
          t("timeExcel.failed_to_read", { fallback: "Failed to read file" }) +
            ": " +
            (err as any).message,
        );
      }
    };
    reader.readAsArrayBuffer(file);
  });

  /* 166 §B — LIFTED, because the PREVIEW needs the same answer `doImport`
     needs. `findCol` is a pure function of `headers` and this alias list, and
     a second copy of the list beside the preview would be a second thing to
     get wrong (the file already says so about `pair`, and 149 §2.2 about the
     Mark column). `doImport` reads this one; nothing else moved. `headers` is
     `[]` until the workbook parses, so this is -1 until then. */
  const typeIdx = findCol(
    headers,
    "Activity Type",
    "activity type",
    "Type",
    "type",
    "Activity",
    ...colNames("activity_type"),
  );

  const doImport = () => {
    const hdr = headers;
    const dateIdx = findCol(hdr, "Date", "date", "DATE", "Datum", ...colNames("date"));
    const collabIdx = findCol(
      hdr,
      "Collaborator",
      "collaborator",
      "Who",
      "Name",
      "Nom",
      ...colNames("collaborator"),
    );
    const descIdx = findCol(hdr, "Description", "description", "Desc", "desc", ...colNames("description"));
    const minIdx = findCol(hdr, "Minutes", "minutes", "Min", "min", "Time", ...colNames("minutes"));
    // 149 §2.2 — `Mark` is the imported billability column (the 3-state field
    // the server accepts); the old `Billed` header stays recognised so sheets
    // already exported keep importing (Yes → billable, No → unmarked).
    const markIdx = findCol(hdr, "Mark", "mark", ...colNames("mark"));
    const billedIdx = findCol(hdr, "Billed", "billed", ...colNames("billed"));
    // 149 §2.2 — columns the import deliberately reads past: derived (Hourly
    // Rate, Amount) or server-owned (Billed Date; Invoice — an invoice binding
    // is the server's, never a spreadsheet's). Reported, not silently dropped.
    const ignoredCols = [
      findCol(hdr, "Hourly Rate (EUR)", "hourly rate", ...colNames("hourly_rate")),
      findCol(hdr, "Amount (EUR)", "amount", ...colNames("amount")),
      findCol(hdr, "Billed Date", "billed date", ...colNames("billed_date")),
      findCol(hdr, "Invoice", "invoice", ...colNames("invoice")),
    ]
      .filter((i) => i >= 0)
      .map((i) => String(hdr[i]));

    // The mark cell, matched across every supported language's labels (the
    // same catalog the export writes from), lowercased like the Billed parse.
    const markCandidates = (state: string, enFallback: string) =>
      new Set(
        [enFallback, state, ...dictTexts(`timeExcel.cell_mark_${state}`)].map((x) =>
          x.toLowerCase(),
        ),
      );
    const MARK_SETS: [string, Set<string>][] = [
      ["billable", markCandidates("billable", "Billable")],
      ["unbillable", markCandidates("unbillable", "Unbillable")],
      ["unmarked", markCandidates("unmarked", "Unmarked")],
    ];

    let imported = 0;
    /* 166 §G.2 — NAME WHAT WAS REFUSED. This was a bare `skipped++`, so a file
       that imported nothing said "(94 ignorées)" and nothing else — no row, no
       value, no reason. Laurent's ruling 6, 2026-09-04: a duration is never
       guessed at; a cell that is neither is refused, BY ROW, WITH ITS VALUE. */
    const skippedRows: { rn: number; who: string; desc: string; reason: string }[] = [];
    /* 166 §G.1 — how the column was read, stated ONCE in the summary. Set by
       the first row that answers; a mixed column reports how the majority of
       its readable cells came in, which is the fact worth seeing. */
    let nTime = 0;
    let nMinutes = 0;
    const newEntries: any[] = [];

    /* The formatted twin is only trustworthy if it is the same list. If the
       reader ever changes its blank-row policy this falls back to the value
       rules alone rather than reading row N's format against row M's value. */
    const fmtOk = fmtRows.length === rows.length;

    for (let ri = 0; ri < rows.length; ri++) {
      const row = rows[ri];
      const vals = hdr.map((h) => row[h]);
      const dateVal = parseDate(vals[dateIdx]);
      // The sheet row number a human reads in Excel: the header is row 1.
      const rn = ri + 2;
      const who = cellText(vals[collabIdx]);
      const desc = cellText(vals[descIdx]).slice(0, 60);
      const refuse = (reason: string) => skippedRows.push({ rn, who, desc, reason });

      if (!dateVal) {
        const _shown = cellText(vals[dateIdx]);
        refuse(
          t("timeExcel.reason_no_date", {
            value: _shown,
            fallback: `no usable date: ${_shown}`,
          }),
        );
        continue;
      }
      const cell = parseMinutesCell(
        vals[minIdx],
        fmtOk && minIdx >= 0 ? fmtRows[ri][hdr[minIdx]] : undefined,
      );
      if (cell.source === "time") nTime++;
      else if (cell.source === "minutes") nMinutes++;
      if (cell.minutes == null || cell.minutes <= 0) {
        refuse(cell.reason ?? t("timeExcel.reason_not_a_duration", { value: "", fallback: "not a duration" }));
        continue;
      }
      /* The 5-minute grid rounds an imported cell, and a value it flattens to
         ZERO is a REFUSAL rather than a silent zero-minute row: a 2-minute cell
         rounds to 0, and the row says so with its own number in it.
         175 §1 — THIS IS NOW THE ONLY 5-MINUTE GRID IN THE TREE. It used to say
         the grid "stays where it was (below, on the entry)"; both entry editors
         dropped it on 2026-09-09 (rounding to a billing increment is the
         lawyer's judgment, not a text box's) and this import was not in that
         ruling's scope. Whether a spreadsheet cell should still be rounded when
         a typed one is not is Laurent's to say; it is named in the PR, not
         decided here. */
      const minVal = Math.round(cell.minutes / 5) * 5;
      if (minVal <= 0) {
        refuse(
          t("timeExcel.reason_rounds_to_zero", {
            n: cell.minutes,
            fallback: `${String(cell.minutes)} min rounds to 0 on the 5-minute grid`,
          }),
        );
        continue;
      }

      // Match collaborator by name
      const collabName = String(vals[collabIdx] || "")
        .trim()
        .toLowerCase();
      let coId = currentUser;
      if (collabName) {
        const found = co.find(
          (c: any) =>
            pname(c).toLowerCase() === collabName ||
            `${c.firstName} ${c.lastName}`.toLowerCase() === collabName ||
            `${c.lastName} ${c.firstName}`.toLowerCase() === collabName,
        );
        if (found) coId = found.id;
      }

      // Match activity type by code
      const typeCode = String(vals[typeIdx] || "")
        .trim()
        .toUpperCase();
      const validType = (tkTypes || []).find((t: any) => t.code === typeCode);

      // 149 §2.2 — the import writes `mark` (billabilityMark, the field the
      // server accepts), never `billed`: §2.1 MEASURED that a `billed` write
      // is dropped by the server's whitelist and the row lands 'unmarked' no
      // matter what the sheet said. Mark column first; the legacy Billed
      // column as fallback (Yes → billable, No → unmarked); neither → the
      // server default, stated explicitly.
      let mark = "unmarked";
      const markVal = markIdx >= 0 ? String(vals[markIdx] || "").trim().toLowerCase() : "";
      if (markVal) {
        const hit = MARK_SETS.find(([, set]) => set.has(markVal));
        if (hit) mark = hit[0];
      } else if (billedIdx >= 0) {
        const billedVal = String(vals[billedIdx] || "")
          .trim()
          .toLowerCase();
        const isBilled = [
          "yes",
          "y",
          "true",
          "1",
          "oui",
          "ja",
          ...dictTexts("timeExcel.cell_yes").map((s) => s.toLowerCase()),
        ].includes(billedVal);
        if (isBilled) mark = "billable";
      }

      newEntries.push({
        id: nidFn([...(te || []), ...newEntries]),
        date: dateVal,
        coId: coId || currentUser,
        type: validType ? validType.code : typeCode || "",
        desc: String(vals[descIdx] || ""),
        // 166 §G.1 — already on the 5-minute grid (and refused if it flattened
        // to zero), so this is the value, not a second rounding.
        min: minVal,
        mark,
        // The client-side mirror, kept by lib/billability's own invariant so
        // the optimistic row renders exactly what the server will echo.
        billed: mark === "billable",
        billedDate: null,
        caId: caseId || null,
        ccId: ccId || null,
      });
      imported++;
    }

    /* 166 §G.1 — HOW THE COLUMN WAS READ, said once. A wrong reading is a
       whole-file event, so it belongs in the sentence that reports the file. */
    let readAs = "";
    if (nTime > 0 || nMinutes > 0) {
      readAs =
        nTime >= nMinutes
          ? t("timeExcel.read_as_time", { fallback: "durations read as time cells" })
          : t("timeExcel.read_as_minutes", { fallback: "durations read as minutes" });
    }
    /* 166 §G.2 — the first few refusals ride the toast; the modal below holds
       the whole list. `#row: reason` is data and punctuation, not a sentence. */
    const firstFew = (n: number): string => {
      if (!skippedRows.length) return "";
      const named = skippedRows
        .slice(0, n)
        .map((r) => `#${String(r.rn)}: ${r.reason}`)
        .join(" · ");
      const rest = skippedRows.length - n;
      const tail =
        rest > 0
          ? " " + t("timeExcel.more_n", { n: rest, fallback: "+{n} more…" })
          : "";
      return " " + named + tail;
    };
    const skipped = skippedRows.length;

    if (imported === 0) {
      toast(
        t("timeExcel.no_valid_entries", withCount(skipped, "skipped", { fallback: `No valid entries found (${skipped} skipped)`,
        })) +
          (readAs ? ` — ${readAs}` : "") +
          firstFew(3),
        // 166 §G.3 — Laurent's ruling 7: a toast that reports a failure is
        // never green. Nothing imported, so this is `error`.
        outcomeVariant({ ok: 0, duplicate: 0, failed: skipped }),
      );
      setOutcome({ imported: 0, total: rows.length, skipped: skippedRows, readAs });
      return;
    }

    let updatedTe;
    const stored: unknown[] = Array.isArray(te) ? te : [];
    const sameMatter = (e: unknown): boolean => {
      const o = e as { caId?: unknown; ccId?: unknown };
      if (caseId) return o.caId === caseId;
      if (ccId) return o.ccId === ccId;
      return false;
    };
    if (importMode === "replace") {
      // Remove existing entries for this case, add new
      updatedTe = [...stored.filter((e) => !sameMatter(e)), ...newEntries];
    } else {
      updatedTe = [...stored, ...newEntries];
    }
    /* 214 addendum 4 §25 [RULED 63 — Laurent, 04/10] — « Replace all entries for this case » DELETES the matter's
       stored entries (the engine's delete): each is listed — its date, its collaborator, its minutes, its description
       — under the matter, and the window waits; « Annuler » writes nothing. Add loses nothing and runs at once. */
    const losses = newLossPlan();
    if (importMode === "replace") {
      const pool: unknown[] = Array.isArray(co) ? co : [];
      const who = (id: unknown): string => {
        const c = pool.find((x) => (x as { id?: unknown }).id === id);
        return c === undefined ? "" : pname(c);
      };
      addRowsTakenUnder(
        losses, "matter", String(matterLabel ?? ""), stored.filter(sameMatter), "deleted",
        (e) => textOfValue((e as { date?: unknown }).date),
        (e) => { const o = e as { coId?: unknown; min?: unknown; desc?: unknown }; return [who(o.coId), `${String(o.min ?? 0)} min`, cellText(o.desc)].filter(Boolean).join(" · "); },
      );
    }
    const write = () => {
    s(SK.te, updatedTe, sTe);
    // 149 §2.2 — the read-past columns ride the summary rather than a second
    // toast, so one message carries the whole verdict of the import.
    const ignoredNote = ignoredCols.length
      ? " " +
        t("timeExcel.import_ignored_cols", {
          cols: ignoredCols.join(", "),
          fallback: `Columns read past (derived or server-owned): ${ignoredCols.join(", ")}.`,
        })
      : "";
    toast(
      t("timeExcel.import_summary", withCount(rows.length, "total", { imported,
        skipped,
        fallback: `Imported ${imported} of ${rows.length} entries${skipped > 0 ? ` (${skipped} skipped — missing required fields)` : ""}`,
      })) +
        (readAs ? ` — ${readAs}` : "") +
        firstFew(2) +
        ignoredNote,
      // 166 §G.3 — success when nothing was refused, warning when some were.
      outcomeVariant({ ok: imported, duplicate: 0, failed: skipped }),
    );
    // 166 §G.2 — a clean import still closes; one with refusals stays open so
    // the rows it would not take can be read and fixed in the file.
    if (skippedRows.length) setOutcome({ imported, total: rows.length, skipped: skippedRows, readAs });
    else onClose();
    };
    if (hasLoss(losses)) {
      setLossAsk({ plan: losses, run: () => { setLossAsk(null); write(); } });
      return;
    }
    write();
  };

  if (error) {
    return (
      <ModalLayout
        title={t("timeExcel.import_error", { fallback: "Import Error" })}
        onClose={onClose}
        showX={false}
        closeOnOverlay
        maxWidth={400}
        cancelLabel={t("common.close", { fallback: "Close" })}
      >
        <p style={{ fontSize: FS.s11, color: "var(--rd)" }}>{error}</p>
      </ModalLayout>
    );
  }

  if (!parsed) {
    return (
      <ModalLayout hideHeader footer={null} maxWidth={400}>
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("timeExcel.reading_file", { fallback: "Reading file…" })}
        </p>
      </ModalLayout>
    );
  }

  if (lossAsk) {
    return (
      <ImportLossDialog
        plan={lossAsk.plan}
        t={t as Translate}
        groupsSentence={(t as Translate)("import.loss.matters", withCount(1, "count", { fallback: "1 matter" }))}
        onAll={lossAsk.run}
        onCancel={() => { setLossAsk(null); }}
      />
    );
  }

  /* 166 §G.2 — THE VERDICT SCREEN. One table for refused rows and not two:
     `FailedRowsList` is the shell §F.1 extracted from FolderPropagationModal,
     widened here by nothing — the sheet row number rides the label, which is
     what the cc import's own three-column table spells as a separate cell
     (CollectiveCaseView.tsx:1799). */
  if (outcome) {
    return (
      <ModalLayout
        title={t("timeExcel.import_time_entries", { fallback: "Import Time Entries" })}
        onClose={onClose}
        showX={false}
        closeOnOverlay
        maxWidth={600}
        cancelLabel={t("common.close", { fallback: "Close" })}
      >
        <p style={{ fontSize: FS.s11, marginBottom: 4 }}>
          {t("timeExcel.import_summary", withCount(outcome.total, "total", { imported: outcome.imported,
            skipped: outcome.skipped.length,
            fallback: `Imported ${outcome.imported} of ${outcome.total} entries`,
          }))}
        </p>
        {outcome.readAs && (
          <p style={{ fontSize: FS.s10, color: "var(--tm)", marginBottom: 10 }}>
            {outcome.readAs}
          </p>
        )}
        <FailedRowsList
          title={t("timeExcel.skipped_rows_title", { fallback: "Skipped rows" })}
          moreLabel={t("timeExcel.more_n", { n: outcome.skipped.length - FAILED_ROWS_MAX, fallback: "+{n} more…" })}
          rows={outcome.skipped.map((r) => ({
            label: `#${r.rn}  ${r.who}${r.desc ? "  " + r.desc : ""}`,
            reason: r.reason,
          }))}
        />
      </ModalLayout>
    );
  }

  const preview = rows.slice(0, 5);

  return (
    <ModalLayout
      title={t("timeExcel.import_time_entries", {
        fallback: "Import Time Entries",
      })}
      onClose={onClose}
      onSave={doImport}
      showX={false}
      closeOnOverlay
      maxWidth={600}
      saveLabel={t("timeExcel.import_n_entries", {
        count: rows.length,
        fallback: `Import ${rows.length} entries`,
      })}
    >
          <p style={{ fontSize: FS.s11, marginBottom: 10 }}>
            {t("timeExcel.found_n_entries", {
              count: rows.length,
              fallback: "Found",
            })}{" "}
            <strong>{rows.length}</strong>{" "}
            {t("timeExcel.entries_in_file_suffix", {
              fallback: "entries in the file.",
            })}
          </p>

          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: FS.s10,
                fontWeight: 600,
                color: "var(--tm)",
                marginBottom: 6,
              }}
            >
              {t("timeExcel.import_mode", { fallback: "Import mode" })}
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: FS.s11,
                cursor: "pointer",
                marginBottom: 4,
              }}
            >
              <input
                type="radio"
                checked={importMode === "add"}
                onChange={() => setImportMode("add")}
              />
              {t("timeExcel.mode_add", { fallback: "Add to existing entries" })}
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: FS.s11,
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                checked={importMode === "replace"}
                onChange={() => setImportMode("replace")}
              />
              <span>
                {t("timeExcel.mode_replace", {
                  fallback: "Replace all entries for this case",
                })}{" "}
                <span style={{ color: "var(--rd)", fontSize: FS.s10 }}>
                  {t("timeExcel.destructive", { fallback: "(destructive)" })}
                </span>
              </span>
            </label>
          </div>

          <div
            style={{
              fontSize: FS.s10,
              fontWeight: 600,
              color: "var(--tm)",
              marginBottom: 4,
            }}
          >
            {t("timeExcel.preview_first_n", {
              count: Math.min(5, rows.length),
              fallback: `Preview (first ${Math.min(5, rows.length)} rows)`,
            })}
          </div>
          <div className="table-wrap" style={{ maxHeight: 200, overflowY: "auto" }}>
            <Table style={{ width: "100%" }}>
              <thead>
                <tr>
                  {headers.slice(0, 6).map((h) => (
                    <th
                      key={h}
                      style={{
                        fontSize: FS.s8,
                        padding: "3px 4px",
                        color: "var(--tm)",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i}>
                    {headers.slice(0, 6).map((h, hi) => {
                      /* 166 §B — THE THIRD SURFACE, and the earliest one: the
                         code is marked BEFORE the import commits, so a wrong
                         column or a stale export is visible while the choice
                         is still cancellable. Same rule as the table's chip
                         and the modal's select — the app did not produce this
                         value, so it wears `--st`.

                         `hi` indexes the SLICE, which starts at 0, so it is
                         the index in `headers` as well; an activity column
                         past the sixth is simply not previewed and asks
                         nothing. The comparison is `doImport`'s own
                         (`trim().toUpperCase()` against `tkTypes[].code`,
                         :318-324 below), so the preview cannot say one thing
                         and the import do another.

                         The hint is `taxonomy.option.unlisted`, NOT
                         `taxonomy.orphan.hint`: nothing is stored yet and
                         there is nothing to re-pick here, so the orphan hint's
                         instruction would be one this screen cannot obey.
                         Both keys already exist in en/fr/nl
                         (`FieldSelect.tsx:196-213` composes the pair) — no
                         key is minted. */
                      const raw = String(row[h] || "");
                      const unlisted =
                        hi === typeIdx &&
                        !!raw.trim() &&
                        (tkTypes || []).length > 0 &&
                        !(tkTypes || []).some(
                          (x: any) => x.code === raw.trim().toUpperCase(),
                        );
                      return (
                        <td
                          key={h}
                          title={
                            unlisted
                              ? t("taxonomy.option.unlisted", {
                                  value: raw.trim().toUpperCase(),
                                  fallback: `${raw.trim().toUpperCase()} — not in the list`,
                                })
                              : undefined
                          }
                          style={{
                            fontSize: FS.s9,
                            padding: "2px 4px",
                            maxWidth: 100,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            ...(unlisted
                              ? { color: "var(--st)", cursor: "help" }
                              : {}),
                          }}
                        >
                          {raw}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
      </ModalLayout>
  );
}
